/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { memo, useContext, useState } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { ApiContext } from '../../../../context/ApiContext';
import {
  KPI_PERCENTAGE_DECIMAL_PRECISION,
  PERCENT_CHANGE_THRESHOLD
} from '../../../shared/Utils/Constants';
import {
  IAny,
  IEmaasEnergyUsageComponentDataArea,
  IEmaasEnergyUsageComponentDataAreaV2
} from '../../IEnergyManagementProps';
import {
  convertDataForBreakdownByMeter,
  convertDataForBreakdownByMeterWithIndividualScaling,
  convertUnitForPerformanceGraph,
  convertUnitForPerformanceGraphV2,
  getPerformanceGraphData,
  getPerformanceGraphDataV2,
  getSpanDetails
} from '../../../shared/Utils/Utils';
import { getProgressBarUnitScaledData } from '../../../shared/Utils/portfolioDataFormatter';
import { ConversionType } from '../../../shared/Utils/unitHandling/UnitModel';
import {
  getNullCheckedAggregationEnergyCardData,
  getNullToZero
} from '../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import { getEnergyEndDate } from '../../../shared/Utils/PortfolioEnergy/PortfolioEnergyVDUtils';
import { IDataTableHeader } from '../../../shared/BreakdownByMeter/BreakdownByMeterProps';
import CommonEnergyComponent from '../../../shared/EnergyKPI/CommonEnergyComponent';
import { EnergyUsageField } from '../../../shared/Enums';
interface IEmaasEnergyUsageComponentProps {
  data: IEmaasEnergyUsageComponentDataAreaV2;
  viewType?: string;
  emaasContext?: IAny;
}

export const EmaasNILMEnergyUsageComponent = (
  props: IEmaasEnergyUsageComponentProps
) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { data, emaasContext } = props;
  const apiContext = useContext(ApiContext);
  const {
    onEnergyUsageViewDetailsClick,
    isBreakdownEquipment,
    userPreferedUnit
  } = apiContext;
  const onViewDetailsClick = () => {
    if (onEnergyUsageViewDetailsClick) {
      onEnergyUsageViewDetailsClick();
    }
  };

  let fromUnit: string;
  // if (true) {
  //   fromUnit = userPreferedUnit as any;
  // } else {
  //   fromUnit = energyMeasurementUnit;
  // }

  fromUnit = userPreferedUnit as any;

  const {
    performanceAggregation,
    nilmAggregation,
    aggregationsStartDate,
    aggregationsEndDate,
    performanceAggregationApplied,
    energyMeasurementUnit,
    areaWiseAggregations,
    breakdownByMeter,
    addOn
  } = data;

  const {
    summary: {
      totalConsumption,
      totalBaselineConsumption,
      totalEnergyDeviation,
      totalEnergyDeviationPercentage,
      absoluteTotalBaselineConsumption,
      absoluteTotalConsumption
    }
  } = data.nilmAggregation as IAny;

  const SpanDetails = getSpanDetails(
    aggregationsStartDate,
    aggregationsEndDate
  );

  const breakDownByDisaggreegatedMeter = nilmAggregation.equipmentTypeAggregation.map(
    (eqp) => {
      return {
        // ...eqp,
        name: eqp.sourceType,
        id: '',
        sourceType: 'Electric',
        totalConsumption: eqp.totalConsumption,
        totalBaselineConsumption: eqp.totalBaselineConsumption,
        totalAbsoluteConsumption: null,
        totalAbsoluteBaselineConsumption: null,
        totalNativeAbsoluteActualConsumption: null,
        totalNativeAbsolutePredictedConsumption: null,
        totalEnergyDeviation: eqp.totalEnergyDeviation,
        totalEnergyDeviationPercentage: eqp.totalEnergyDeviationPercentage,
        totalNativeConsumption: null,
        totalNativeBaselineConsumption: null,
        totalNativeEnergyDeviation: null,
        totalNativeEnergyDeviationPercentage: null
      };
    }
  );

  const consumptionBreakdownMeterData = convertDataForBreakdownByMeterWithIndividualScaling(
    props.viewType === 'nilm'
      ? breakDownByDisaggreegatedMeter
      : isBreakdownEquipment
      ? breakdownByMeter
      : areaWiseAggregations,
    fromUnit,
    [],
    energyMeasurementUnit,
    apiContext.showChangeWhenData
  );

  const convertedPerformanceData = convertUnitForPerformanceGraphV2(
    nilmAggregation.energyUsageAggregation,
    fromUnit //energyMeasurementUnit
  );

  const { values } = consumptionBreakdownMeterData;

  const { displayUnit: performanceDisplayUnit } = convertedPerformanceData;

  const [usageType, setUsageType] = useState<string>(t(d.HIGHWITHOUTUNIT));

  const progressBarUnitScaledData = getProgressBarUnitScaledData(
    ConversionType.ELECTRICITY,
    { previousPeriod: totalBaselineConsumption, actual: totalConsumption },
    totalEnergyDeviation,
    fromUnit, //energyMeasurementUnit,
    undefined,
    absoluteTotalBaselineConsumption,
    absoluteTotalConsumption,
    energyMeasurementUnit,
    apiContext.showChangeWhenData,
    true
  );
  const comparisonData = getPerformanceGraphDataV2(
    nilmAggregation.energyUsageAggregation,
    energyMeasurementUnit,
    performanceDisplayUnit,
    data.intervalType,
    ConversionType.ELECTRICITY
  );

  const isPercentageGreater =
    Math.abs(
      Number(
        getNullToZero(totalEnergyDeviationPercentage)?.toFixed(
          KPI_PERCENTAGE_DECIMAL_PRECISION
        )
      )
    ) > PERCENT_CHANGE_THRESHOLD;

  const aggregationEnergyData = {
    actual: progressBarUnitScaledData.actual,
    previousPeriod: progressBarUnitScaledData.previousPeriod,
    absoluteActual: progressBarUnitScaledData.absoluteActual,
    absolutePrevPeriod: progressBarUnitScaledData.absolutePreviousPeriod,
    savings:
      apiContext.showChangeWhenData &&
      progressBarUnitScaledData.deviation !== null &&
      isPercentageGreater
        ? progressBarUnitScaledData.deviation
        : progressBarUnitScaledData.deviation,
    actualUnit: progressBarUnitScaledData.actualUnit,
    baselineUnit: progressBarUnitScaledData.baselineUnit,
    changeUnit: progressBarUnitScaledData.changeUnit,
    savingsPercentage: parseFloat(
      getNullToZero(totalEnergyDeviationPercentage)?.toFixed(
        KPI_PERCENTAGE_DECIMAL_PRECISION
      )
    )
  };
  const headers = [];

  headers.push(
    {
      HeaderValue: t(d.BREAKDOWNBYEQUIPMENTTYPE),
      TextAlignValue: 'left',
      Field: 'type'
    },
    {
      HeaderValue: t(d.HIGHWITHOUTUNIT),
      TextAlignValue: 'right',
      Field: 'high',
      sortable: true
    }
  );

  const isNullCheckedEnergyCardData = getNullCheckedAggregationEnergyCardData(
    aggregationEnergyData,
    data?.nilmAggregation?.summary as IAny,
    apiContext.showChangeWhenData
  );
  const onSortingHandler = (sortOrder: number): void => {
    sortOrder === 1
      ? setUsageType(t(d.LOWWITHNOUNIT))
      : setUsageType(t(d.HIGHWITHOUTUNIT));
  };
  const energyComponentProps = {
    isNILM: true,
    onViewDetailsClick: onViewDetailsClick,
    progressBarData: {
      aggregationData: isNullCheckedEnergyCardData,
      unit: progressBarUnitScaledData.ConvertedUnit,
      deviationScaledData: progressBarUnitScaledData.deviationScaledData
    },
    graphData: {
      chartSeries: comparisonData,
      startTime: aggregationsStartDate,
      endTime: aggregationsEndDate,
      aggregationType: data.intervalType
    },
    tableData: {
      columnHeaders: headers as IDataTableHeader[],
      meterData: values
    },
    dateData: SpanDetails,
    onSortingHandler: onSortingHandler,
    emaasContext: emaasContext,
    nilmCheck: true,
    energyMeasurementUnit,
    fromCarbonKPI: true,
    originalUnit: energyMeasurementUnit,
    conversionType: ConversionType.ELECTRICITY
  };

  return <CommonEnergyComponent {...energyComponentProps} />;
};
export default memo(EmaasNILMEnergyUsageComponent);
